import { Component, Input, OnInit, SecurityContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';


@Component({
  selector: 'app-details-tab',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    TableModule
  ],
  templateUrl: './details-tab.component.html',
  styleUrl: './details-tab.component.scss'
})
export class DetailsTabComponent implements OnInit {
  
  // Traditional properties for reactive state management
  visible: boolean = false;
  modalHeader: string = '';
  modalContent: string = '';
  isDarkTheme: boolean = false;
  
  // Input properties (if needed from parent)
  @Input() jobDetails: any;
  
  constructor(private sanitizer: DomSanitizer) {}
  
  // Getter for sanitized content
  get sanitizedModalContent(): String {
    const content = this.modalContent;
    if (!content) return '';
    
    const cleanContent = this.cleanHtmlContent(content);
    const transformedContent = this.sanitizer.sanitize(SecurityContext.HTML, cleanContent || '');
    return transformedContent || '';
  }
  
  ngOnInit() {
    // Initialize theme detection
  }
  
  openDetails(event: Event, type: string) {
    event.preventDefault();
    
    switch (type) {
      case 'description':
        this.modalHeader = 'Job Description';
        this.modalContent = this.jobDetails?.description || '<p>No description available.</p>';
        break;
      case 'requirements':
        this.modalHeader = 'Job Requirements';
        this.modalContent = this.jobDetails?.requirement || '<p>No requirements specified.</p>';
        break;
      case 'responsibilities':
        this.modalHeader = 'Job Responsibilities';
        this.modalContent = this.jobDetails?.responsibility || '<p>No responsibilities specified.</p>';
        break;
      default:
        this.modalHeader = 'Details';
        this.modalContent = '<p>No content available.</p>';
    }
    
    this.visible = true;
  }
  
  closeDialog() {
    this.visible = false;
    // Clear content after animation completes
    setTimeout(() => {
      this.modalContent = '';
      this.modalHeader = '';
    }, 300);
  }
  
  private cleanHtmlContent(html: string): string {
    if (!html) return '';
    
    // Remove potentially dangerous elements and attributes
    let cleaned = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframes
      .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '') // Remove event handlers
      .replace(/javascript:/gi, '') // Remove javascript: protocols
      .replace(/vbscript:/gi, '') // Remove vbscript: protocols
      .replace(/data:text\/html/gi, ''); // Remove data:text/html
    
    // Ensure proper paragraph structure
    cleaned = this.ensureParagraphStructure(cleaned);
    
    // Add proper line breaks and spacing
    cleaned = this.improveTextFormatting(cleaned);
    
    return cleaned;
  }
  
  private ensureParagraphStructure(html: string): string {
    // If content doesn't have proper paragraph tags, wrap in paragraphs
    if (!html.includes('<p>') && !html.includes('<div>') && !html.includes('<h')) {
      // Split by double line breaks and wrap each section in paragraphs
      return html
        .split(/\n\s*\n/)
        .filter(paragraph => paragraph.trim())
        .map(paragraph => `<p>${paragraph.trim()}</p>`)
        .join('');
    }
    return html;
  }
  
private improveTextFormatting(html: string): string {
    return html
      .replace(/\n/g, '<br>') // Convert line breaks to <br> tags
      .replace(/\s{2,}/g, ' ') // Replace multiple spaces with single space
      .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces with regular spaces
      .replace(/<br\s*\/?>\s*<br\s*\/?>/g, '</p><p>'); // Convert double <br> to paragraphs
  }

  
  // Utility method to truncate content if needed
  truncateContent(content: string, maxLength: number = 1000): string {
    if (content.length <= maxLength) return content;
    
    let truncated = content.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastSpace > 0) {
      truncated = truncated.substring(0, lastSpace);
    }
    
    return truncated + '...';
  }
}
